import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  Car,
  CheckCircle,
  X,
  Clock,
  RefreshCw,
  ExternalLink,
  FileText,
  User,
  Mail,
  Phone,
  AlertCircle
} from 'lucide-react';

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-amber-500', icon: Clock },
  approved: { label: 'Approved', color: 'bg-emerald-500', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-500', icon: X },
  suspended: { label: 'Suspended', color: 'bg-red-700', icon: AlertCircle }
};

const AdminCarpoolDrivers = () => {
  const { token } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API}/carpool/driver-applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data);
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, [token]);

  const handleStatusUpdate = async (profileId, status) => {
    setUpdating(true);
    try {
      await axios.patch(
        `${API}/carpool/driver-profile/${profileId}/status?status=${status}${adminNotes ? `&admin_notes=${encodeURIComponent(adminNotes)}` : ''}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Application ${status}`);
      setSelectedApp(null);
      setAdminNotes('');
      fetchApplications();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const pendingCount = applications.filter(a => a.status === 'pending').length;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Car className="w-6 h-6 text-emerald-500" /> Carpool Drivers
        </h1>
        <p className="text-slate-600">Manage driver applications for Killarney Rides</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="py-4">
            <p className="text-amber-600 font-heading font-bold text-2xl">{pendingCount}</p>
            <p className="text-slate-600">Pending Review</p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="py-4">
            <p className="text-emerald-600 font-heading font-bold text-2xl">
              {applications.filter(a => a.status === 'approved').length}
            </p>
            <p className="text-slate-600">Approved Drivers</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="py-4">
            <p className="text-slate-600 font-heading font-bold text-2xl">{applications.length}</p>
            <p className="text-slate-600">Total Applications</p>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      ) : applications.length === 0 ? (
        <Card className="bg-white">
          <CardContent className="py-12 text-center">
            <Car className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No driver applications yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {applications.map((app) => {
            const status = statusConfig[app.status] || statusConfig.pending;
            const StatusIcon = status.icon;
            
            return (
              <Card key={app.id} className="bg-white" data-testid={`driver-app-${app.id}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-start gap-6">
                    {/* User Info */}
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-plp-gold rounded-full flex items-center justify-center">
                        <span className="text-2xl font-bold text-plp-blue">
                          {app.user?.name?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-lg">{app.user?.name}</h3>
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {app.user?.email}
                        </p>
                        {app.user?.phone && (
                          <p className="text-sm text-slate-500 flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {app.user.phone}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Vehicle Info */}
                    <div className="flex-1">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-slate-500">Vehicle</p>
                          <p className="font-medium">
                            {app.vehicle_year} {app.vehicle_make} {app.vehicle_model}
                          </p>
                          <p className="text-sm text-slate-600">{app.vehicle_color}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">License Plate</p>
                          <p className="font-medium">{app.license_plate}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Available Seats</p>
                          <p className="font-medium">{app.seats_available}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Applied</p>
                          <p className="font-medium">{new Date(app.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>

                      {app.bio && (
                        <div className="mt-4">
                          <p className="text-sm text-slate-500">About</p>
                          <p className="text-slate-700">{app.bio}</p>
                        </div>
                      )}

                      {/* Documents */}
                      <div className="mt-4 flex gap-4">
                        {app.license_doc_url && (
                          <a href={app.license_doc_url} target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" size="sm">
                              <FileText className="w-4 h-4 mr-1" /> License Doc
                              <ExternalLink className="w-3 h-3 ml-1" />
                            </Button>
                          </a>
                        )}
                        {app.insurance_doc_url && (
                          <a href={app.insurance_doc_url} target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" size="sm">
                              <FileText className="w-4 h-4 mr-1" /> Insurance
                              <ExternalLink className="w-3 h-3 ml-1" />
                            </Button>
                          </a>
                        )}
                        {app.vehicle_photo_url && (
                          <a href={app.vehicle_photo_url} target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" size="sm">
                              <Car className="w-4 h-4 mr-1" /> Vehicle Photo
                              <ExternalLink className="w-3 h-3 ml-1" />
                            </Button>
                          </a>
                        )}
                      </div>

                      {app.admin_notes && (
                        <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                          <p className="text-sm text-slate-500">Admin Notes</p>
                          <p className="text-slate-700">{app.admin_notes}</p>
                        </div>
                      )}
                    </div>

                    {/* Status & Actions */}
                    <div className="flex flex-col items-end gap-3">
                      <Badge className={`${status.color} text-white`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {status.label}
                      </Badge>

                      {app.status === 'pending' && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button 
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => setSelectedApp(app)}
                              data-testid={`review-driver-${app.id}`}
                            >
                              Review Application
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle className="font-heading">Review Driver Application</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 mt-4">
                              <div className="p-4 bg-slate-50 rounded-lg">
                                <p className="font-medium">{app.user?.name}</p>
                                <p className="text-sm text-slate-500">
                                  {app.vehicle_year} {app.vehicle_make} {app.vehicle_model}
                                </p>
                              </div>

                              <div>
                                <Label>Admin Notes (Optional)</Label>
                                <Textarea
                                  value={adminNotes}
                                  onChange={(e) => setAdminNotes(e.target.value)}
                                  placeholder="Add notes for this decision..."
                                  rows={3}
                                />
                              </div>

                              <div className="flex gap-3">
                                <Button
                                  onClick={() => handleStatusUpdate(app.id, 'rejected')}
                                  disabled={updating}
                                  variant="outline"
                                  className="flex-1 text-red-500 hover:bg-red-50"
                                >
                                  <X className="w-4 h-4 mr-1" /> Reject
                                </Button>
                                <Button
                                  onClick={() => handleStatusUpdate(app.id, 'approved')}
                                  disabled={updating}
                                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                                  data-testid="approve-driver-btn"
                                >
                                  {updating ? (
                                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                                  ) : (
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                  )}
                                  Approve
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}

                      {app.status === 'approved' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-500"
                          onClick={() => handleStatusUpdate(app.id, 'suspended')}
                        >
                          Suspend Driver
                        </Button>
                      )}

                      {app.status === 'suspended' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-emerald-500"
                          onClick={() => handleStatusUpdate(app.id, 'approved')}
                        >
                          Reinstate
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminCarpoolDrivers;
